import type { CurrentUser } from '@/types/user.type';

/**
 * Check if a user has a specific permission
 */
export function hasPermission(user: CurrentUser | undefined, permissionName: string): boolean {
  if (!user?.permissions) {
    return false;
  }

  return user.permissions.some(permission => permission === permissionName);
}

/**
 * Check if a user has at least one permission from a list
 */
export function hasAnyPermission(
  user: CurrentUser | undefined,
  permissionNames: string[]
): boolean {
  if (!permissionNames || permissionNames.length === 0) {
    return true;
  }

  if (!user?.permissions) {
    return false;
  }

  return permissionNames.some(permissionName => hasPermission(user, permissionName));
}

/**
 * Check if a user has access to a component based on required permissions
 */
export function canAccessComponent(
  user: CurrentUser | undefined,
  options: {
    requiredPermissions: string[];
  }
): boolean {
  const { requiredPermissions } = options;

  if (!requiredPermissions || requiredPermissions.length === 0) {
    return true;
  }

  return requiredPermissions.some(permission => hasPermission(user, permission));
}

/**
 * Get all permission names for a user
 */
export function getUserPermissions(user: CurrentUser | undefined): string[] {
  if (!user?.permissions) {
    return [];
  }

  return user.permissions;
}
